import { createElement as _createElement } from "@emotion/react";
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AISummary } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/components/AISummary/index.js';
import { RatingGroups } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/components/RatingGroups/index.js';
import { ReviewItem } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/components/ReviewItem.js';
import { ReviewsHeader } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/components/ReviewsHeader.js';
import { Summary } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/components/Summary/index.js';
import { fetchRating } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/utils/fetchRating.js';
import { useConfig } from '../../../../components/HotelDetailsOverlay/context/Config.js';
import { Pagination, PaginationItem, Flex } from '../../../../primitives/index.js';
export const AmadeusRatingSection = ({
  data: {
    hotelId,
    url
  },
  onSelectReview
}) => {
  const {
    t
  } = useTranslation('hotelDetailsOverlay');
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const pageCount = Math.ceil((data?.count || 0) / 10);
  const [traveledAs, setTraveledAs] = useState();
  const [sort, setSort] = useState('latest');
  const [loading, setLoading] = useState(true);
  const headerWrapper = useRef(null);
  const {
    amadeusRating: {
      recommendationRateEnabled,
      reviewSource,
      splitTreatment,
      language,
      onClickReviewImage
    }
  } = useConfig();
  const handleFilterChange = nextFilter => {
    setTraveledAs(nextFilter);
    setPage(1);
  };
  const handleSortChange = nextSort => {
    setSort(nextSort);
    setPage(1);
  };
  const getRating = useCallback(async () => {
    setLoading(true);
    const rating = await fetchRating({
      url,
      hotelId,
      page,
      sort,
      traveledAs,
      splitTreatment,
      lang: language,
      src: reviewSource
    });
    setData(rating);
    setLoading(false);
  }, [url, hotelId, page, traveledAs, sort, splitTreatment, language, reviewSource]);
  useEffect(() => {
    getRating();
  }, [getRating]);
  if (!loading && !data) {
    return _jsx("div", {
      children: t('amadeusRatingSection.noRatings')
    });
  }
  /**
   * We want to highlight (stable AND in that order):
   * - the most recent review
   * - AND the first review marked as "review_laureate" in the field "reviewCompetition" (if one exists)
   *
   * @TODO
   * Currently the "review_laureate" can occur in any position within the list of reviews.
   * That means we'd have to fetch ALL reviews to find the first "review_laureate".
   * But that makes the api pagination kind of pointless plus of course we'd be requesting
   * quite a lot of data.
   */
  const highlightedReviews = [];
  return _jsxs(Flex, {
    spacing: "pages/small",
    children: [_jsx(Summary, {
      loading: loading,
      rating: data?.rating || 0,
      recommendation: recommendationRateEnabled && data?.recommendation != null ? data.recommendation : null,
      reviewsCount: data?.count || 0,
      withLegalDisclaimer: reviewSource !== 'OwnCEE'
    }), _jsxs(Flex, {
      spacing: "dividers/medium",
      divider: true,
      children: [_jsx(RatingGroups, {
        loading: loading,
        groups: data?.ratingGroups
      }), data?.summary && _jsx(AISummary, {
        summaryText: data.summary
      }), highlightedReviews.length > 0 && _jsx(Flex, {
        spacing: "pages/small",
        children: highlightedReviews.map((review, index) => _jsx(ReviewItem, {
          review: review,
          highlighted: true,
          onClickDetailButton: () => {
            onSelectReview(review);
          },
          onClickImage: imageIndex => {
            onClickReviewImage?.({
              review,
              imageIndex
            });
          }
        }, index))
      }), _jsxs(Flex, {
        ref: headerWrapper,
        spacing: "pages/small",
        children: [_jsx(ReviewsHeader, {
          reviewsCount: data?.count || 0,
          onFilterChange: handleFilterChange,
          onSortChange: handleSortChange,
          singleItem: (data?.reviews?.length ?? 0) < 2
        }), _jsx(Flex, {
          spacing: "dividers/medium",
          divider: true,
          children: data?.reviews?.map((review, index) => _jsx(ReviewItem, {
            review: review,
            onClickDetailButton: () => {
              onSelectReview(review);
            },
            onClickImage: imageIndex => {
              onClickReviewImage?.({
                review,
                imageIndex
              });
            }
          }, index))
        })]
      }), pageCount > 1 && _jsx(Pagination, {
        page: page,
        pageCount: pageCount,
        size: "small",
        label: {
          previous: t('amadeusRatingSection.previous'),
          next: t('amadeusRatingSection.next')
        },
        renderItem: props => _createElement(PaginationItem, {
          ...props,
          key: `${props.type}_${props.page}_${props.label}_${props.selected}`,
          // @ts-expect-error ...
          style: {
            background: props.disabled ? 'transparent' : undefined
          }
        }),
        onChange: (_event, page) => {
          headerWrapper.current?.scrollIntoView({
            behavior: 'smooth'
          });
          setPage(page);
        }
      })]
    })]
  });
};